import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { 
  Send, 
  Loader2, 
  MessageSquare, 
  Users, 
  TrendingUp,
  Sparkles,
  Lock,
  ChevronRight,
  CheckCircle,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Example prompts to guide users
const EXAMPLE_PROMPTS = [
  "My team resists a process change I need to implement...",
  "I need to give difficult feedback to a senior developer who's technically brilliant but struggles with collaboration...",
  "My manager wants daily updates but it feels like micromanagement..."
];

const DemoChat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [questionCount, setQuestionCount] = useState(0);
  const [showGate, setShowGate] = useState(false);
  const [ghostFeatures, setGhostFeatures] = useState(null);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch(`${API_URL}/api/demo/check-cookie`, {
          credentials: 'include'
        });
        const data = await res.json();
        
        if (data.has_session && !data.converted) {
          // Restore session
          const sessionRes = await fetch(`${API_URL}/api/demo/session/${data.session_id}`);
          const sessionData = await sessionRes.json();
          
          if (sessionData.found) {
            setSessionId(data.session_id);
            setQuestionCount(sessionData.question_count || 0);
            
            // Restore messages
            const restoredMessages = sessionData.messages?.map((msg, idx) => ({
              id: idx,
              role: msg.role === 'user' ? 'user' : 'assistant',
              content: msg.content
            })) || [];
            setMessages(restoredMessages);
            
            if (sessionData.question_count >= 3) {
              setShowGate(true);
            }
          }
        }
      } catch (err) {
        console.error('Error checking session:', err);
      }
    };
    
    checkSession();
  }, []);

  const handleSend = async () => {
    if (!input.trim() || loading || showGate) return;

    const userMessage = input.trim();
    setInput('');
    setError(null);
    
    // Add user message immediately
    const newUserMessage = {
      id: messages.length,
      role: 'user',
      content: userMessage
    };
    setMessages(prev => [...prev, newUserMessage]);
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/demo/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          question_number: questionCount + 1
        })
      });

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();
      
      setSessionId(data.session_id);
      setQuestionCount(data.question_number);
      
      if (data.ghost_features) {
        setGhostFeatures(data.ghost_features);
      }
      
      if (data.show_gate) {
        setShowGate(true);
      }

      // Add assistant message
      const assistantMessage = {
        id: messages.length + 1,
        role: 'assistant',
        content: data.response
      };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (err) {
      console.error('Demo chat error:', err);
      setError('Something went wrong. Please try again.');
      // Remove the user message on error
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (startTrial = true) => {
    if (!email.trim()) {
      setError('Please enter your email');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/demo/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          email: email.trim(),
          name: name.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save');
      }

      const data = await response.json();
      setSaved(true);
      
    } catch (err) {
      console.error('Save error:', err);
      setError(err.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleExampleClick = (prompt) => {
    setInput(prompt);
    inputRef.current?.focus();
  };

  const questionsRemaining = Math.max(0, 3 - questionCount);

  return (
    <section className="py-16 px-4 bg-zinc-900" id="try-companion" data-testid="demo-chat-section">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-red-500/10 text-red-400 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="w-4 h-4" />
            Try the Companion — No Signup
          </div>
          <h2 className="text-3xl font-bold text-white mb-3">
            Experience AI-Powered Coaching
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            Describe a real leadership challenge and get actionable guidance. 
            This is live AI coaching—not a canned demo.
          </p>
        </div>

        {/* Main Chat Container */}
        <div className="bg-zinc-950 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl" data-testid="demo-chat-container">
          
          {/* Chat Messages Area */}
          <div className="min-h-[300px] max-h-[450px] overflow-y-auto p-6" data-testid="demo-chat-messages">
            {messages.length === 0 ? (
              <div className="space-y-4">
                <div className="text-center py-8">
                  <MessageSquare className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                  <p className="text-zinc-400 mb-6">
                    What leadership challenge can I help you think through?
                  </p>
                  
                  {/* Example Prompts */}
                  <div className="space-y-3">
                    <p className="text-xs text-zinc-600 uppercase tracking-wider">Try one of these:</p>
                    {EXAMPLE_PROMPTS.map((prompt, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleExampleClick(prompt)}
                        data-testid={`demo-example-prompt-${idx}`}
                        className="block w-full text-left px-4 py-3 bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-zinc-700 rounded-lg text-zinc-400 hover:text-zinc-200 text-sm transition-all"
                      >
                        "{prompt}"
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-red-600 text-white'
                          : 'bg-zinc-800 text-zinc-100'
                      }`}
                    >
                      {msg.role === 'assistant' ? (
                        <div className="prose prose-sm prose-invert max-w-none text-sm leading-relaxed [&>p]:mb-3 [&>p:last-child]:mb-0 [&>ul]:mt-1 [&>ul]:mb-3 [&>ol]:mt-1 [&>ol]:mb-3 [&>li]:mb-1">
                          <ReactMarkdown>{msg.content}</ReactMarkdown>
                        </div>
                      ) : (
                        <p className="whitespace-pre-wrap text-sm leading-relaxed">{msg.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-zinc-800 rounded-2xl px-4 py-3">
                      <Loader2 className="w-5 h-5 text-zinc-400 animate-spin" />
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          {/* Ghost Features Preview (shown after Q2) */}
          {ghostFeatures && !showGate && (
            <div className="px-6 py-3 border-t border-amber-500/20 bg-amber-950/30 relative">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-2 text-amber-400 text-xs font-medium">
                  <Users className="w-4 h-4" />
                  <span>People Context</span>
                  <Lock className="w-3 h-3 opacity-60" />
                </div>
                {ghostFeatures.people_context?.map((person, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedPerson(selectedPerson?.name === person.name ? null : person)}
                    data-testid={`demo-person-badge-${idx}`}
                    className="px-3 py-1 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/30 rounded-full text-amber-200 text-xs font-medium cursor-pointer transition-all hover:scale-105"
                  >
                    {person.name}
                    {person.mentions > 1 && (
                      <span className="ml-1 text-amber-400/70">({person.mentions})</span>
                    )}
                  </button>
                ))}
                <span className="text-amber-500/60 italic text-xs">
                  Full version tracks everyone you mention
                </span>
              </div>
              
              {/* Person detail popup */}
              {selectedPerson && (
                <div className="absolute bottom-full left-6 mb-2 w-72 bg-zinc-800 border border-amber-500/30 rounded-lg shadow-xl p-4 z-10" data-testid="demo-person-popup">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center">
                        <Users className="w-4 h-4 text-amber-400" />
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">{selectedPerson.name}</p>
                        <p className="text-amber-400/80 text-xs capitalize">{(selectedPerson.relationship || 'colleague').replace('_', ' ')}</p>
                      </div>
                    </div>
                    <button onClick={() => setSelectedPerson(null)} className="text-zinc-500 hover:text-zinc-300">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-zinc-400 text-xs leading-relaxed mb-2">{selectedPerson.context}</p>
                  <div className="flex items-center gap-3 text-xs text-zinc-500 border-t border-zinc-700 pt-2 mt-2">
                    <span>Mentioned {selectedPerson.mentions}x</span>
                    <span className="flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Full insights in trial
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Conversion Gate (shown after Q3) */}
          {showGate && !saved ? (
            <div className="p-6 border-t border-zinc-800 bg-gradient-to-b from-zinc-900 to-zinc-950">
              <div className="text-center mb-6">
                <h3 className="text-xl font-bold text-white mb-2">
                  Want to continue this conversation?
                </h3>
                <p className="text-zinc-400 text-sm max-w-md mx-auto">
                  This is just the beginning. The real power comes from continuity—every reflection 
                  builds context, and patterns emerge over time.
                </p>
              </div>

              {/* Features list */}
              <div className="grid grid-cols-2 gap-3 mb-6 max-w-md mx-auto">
                {[
                  'Unlimited reflections via email & web',
                  'Pattern recognition over time',
                  'People context that builds',
                  'Weekly summaries & insights'
                ].map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-zinc-300">
                    <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {/* Email Form */}
              <div className="max-w-sm mx-auto space-y-3">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  data-testid="demo-gate-name-input"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
                />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Your email"
                  data-testid="demo-gate-email-input"
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-red-500"
                  required
                />
                
                {error && (
                  <p className="text-red-400 text-sm text-center" data-testid="demo-gate-error">{error}</p>
                )}
                
                <button
                  onClick={() => handleSave(true)}
                  disabled={saving || !email.trim()}
                  data-testid="demo-gate-submit-button"
                  className="w-full py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Starting your trial...
                    </>
                  ) : (
                    <>
                      Start 14-Day Free Trial
                      <ChevronRight className="w-4 h-4" />
                    </>
                  )}
                </button>
                
                <p className="text-zinc-500 text-xs text-center">
                  Your conversation will be saved and emailed to you
                </p>
              </div>
            </div>
          ) : saved ? (
            <div className="p-8 border-t border-zinc-800 bg-gradient-to-b from-green-900/20 to-zinc-950 text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-white mb-2">
                You're all set!
              </h3>
              <p className="text-zinc-400 mb-4">
                Check your email for the conversation transcript and login link.
              </p>
              <a
                href="/login"
                className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg transition-colors"
              >
                Go to Dashboard
                <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          ) : (
            /* Input Area (hidden when gate is showing) */
            <div className="p-4 border-t border-zinc-800">
              <div className="flex items-center gap-3">
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSend()}
                    placeholder="Describe your leadership challenge..."
                    disabled={loading}
                    data-testid="demo-chat-input"
                    className="w-full px-4 py-3 bg-zinc-900 border border-zinc-700 rounded-xl text-white placeholder-zinc-500 focus:outline-none focus:border-red-500 disabled:opacity-50"
                  />
                </div>
                <button
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  data-testid="demo-chat-send-button"
                  className="p-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </button>
              </div>
              
              {/* Questions remaining indicator */}
              {messages.length > 0 && questionsRemaining > 0 && (
                <p className="text-zinc-500 text-xs mt-2 text-center">
                  Demo mode: {questionsRemaining} question{questionsRemaining !== 1 ? 's' : ''} remaining
                </p>
              )}
              
              {error && (
                <p className="text-red-400 text-sm mt-2 text-center">{error}</p>
              )}
            </div>
          )}
        </div>

        {/* Bottom note */}
        <p className="text-center text-zinc-500 text-sm mt-6">
          Your data is secure. We never share your reflections with anyone.
        </p>
      </div>
    </section>
  );
};

export default DemoChat;
